'use client';

import { useAuth } from '@morfi/backend-sdk-v2/client';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { type JSX, useEffect, useRef } from 'react';
import {
  INITIAL_REDDIT_IDENTITY,
  initializeRedditPixel,
  type RedditIdentity,
  shouldLoadRedditPixel,
  syncRedditIdentity,
  trackRedditPageVisit,
} from '@/lib/analytics/providers/reddit';
import { useImpersonationStore } from '@/lib/impersonation/store';
import { logger } from '@/lib/telemetry/logger';

export interface RedditPixelProps {
  environment: string | undefined;
}

export function RedditPixel({ environment }: RedditPixelProps): JSX.Element | null {
  const pathname = usePathname();
  const isImpersonating = useImpersonationStore((state) => state.isActive);
  const { user, isAuthenticated, isLoading } = useAuth();
  const identity = useRef<RedditIdentity>(INITIAL_REDDIT_IDENTITY);
  const lastPath = useRef<string | null>(null);
  const isEnabled = shouldLoadRedditPixel({ environment, isImpersonating });
  const signedInUser = isAuthenticated ? user : null;

  // pixel.js reads `window.rdt` once when it runs and stops if it is missing,
  // so the queue goes in with the script instead of waiting for auth.
  useEffect(() => {
    if (isEnabled) initializeRedditPixel();
  }, [isEnabled]);

  // The identity and page-visit effects wait for auth to settle, and identity runs
  // first, so the first page visit carries the signed-in user's keys and never a
  // previous user's.
  useEffect(() => {
    if (!isEnabled || isLoading) return;

    identity.current = syncRedditIdentity(identity.current, signedInUser);
  }, [isEnabled, isLoading, signedInUser]);

  // pixel.js has no history hook, so client-side navigations need their own PageVisit.
  useEffect(() => {
    if (!isEnabled || isLoading || pathname === null) return;

    lastPath.current = trackRedditPageVisit(lastPath.current, pathname);
  }, [isEnabled, isLoading, pathname]);

  if (!isEnabled) return null;

  const onScriptError = (): void =>
    logger.warn('Reddit pixel script failed to load', { vendor: 'reddit' });

  return (
    <Script
      id="reddit-pixel"
      src="https://www.redditstatic.com/ads/pixel.js"
      strategy="afterInteractive"
      onError={onScriptError}
    />
  );
}
